import type { WithTestId } from '@lib/utils/testid'
import Image from 'next/image'
import type { FunctionComponent } from 'react'

export type ContentfulImageData = {
    url?: string | null
    alt?: string | null
    width?: number | null
    height?: number | null
    contentType?: string | null
}

type Props = WithTestId<ContentfulImageData> & {
    /** Required — forces explicit responsive sizing for performance */
    sizes: string
    /** Marks image as above-the-fold (sets eager loading + high fetch priority + preload) */
    priority?: boolean
    className?: string
    /** Fill parent container instead of using intrinsic width/height */
    fill?: boolean
    style?: React.CSSProperties
}

/**
 * Thin wrapper around next/image for Contentful assets.
 * Falls back to plain <img> for SVGs (next/image optimization can break vectors).
 * Returns null when url is falsy.
 */
export const ContentfulImage: FunctionComponent<Props> = ({
    url,
    alt,
    width,
    height,
    contentType,
    sizes,
    priority,
    className,
    fill,
    style,
    'data-testid': testId,
}) => {
    if (!url) return null

    if (contentType === 'image/svg+xml') {
        return (
            // eslint-disable-next-line @next/next/no-img-element
            <img
                src={url}
                alt={alt ?? ''}
                width={fill ? undefined : (width ?? undefined)}
                height={fill ? undefined : (height ?? undefined)}
                loading={priority ? undefined : 'lazy'}
                className={className}
                style={style}
                data-testid={testId}
            />
        )
    }

    return (
        <Image
            src={url}
            alt={alt ?? ''}
            width={fill ? undefined : (width ?? 800)}
            height={fill ? undefined : (height ?? 600)}
            sizes={sizes}
            priority={priority}
            className={className}
            fill={fill}
            style={style}
            data-testid={testId}
        />
    )
}
